import { formatUnits } from 'viem'

export const DECIMAL_SCALE = 10000

export function fromDecimal(decimal: number): number {
  const scaled = Math.round(decimal * DECIMAL_SCALE)
  return scaled / (DECIMAL_SCALE / 100)
}

export function toDecimal(percentage: number): number {
  const scaled = Math.round(percentage * DECIMAL_SCALE)
  return scaled / (DECIMAL_SCALE * 100)
}

export function formatDecimalToPercentage(
  decimal: number,
  options?: Intl.NumberFormatOptions & { fractionDigits?: number }
): string {
  const { fractionDigits = 2 } = options ?? {}
  const percentageFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    notation: 'compact',
    ...options,
  })
  return `${percentageFormatter.format(fromDecimal(decimal))}%`
}

export function getTokenNumberValue({
  value,
  decimals,
}: { value: string | bigint | undefined; decimals: number }): number {
  return Number(formatUnits(BigInt(value ?? 0), decimals))
}

const defaultTokenFormatterOptions: Intl.NumberFormatOptions = {
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}

export function formatTokenValue(
  { value, decimals }: { value: string | bigint | undefined; decimals: number },
  options?: Intl.NumberFormatOptions
): string {
  const formatter = new Intl.NumberFormat('en-US', {
    ...defaultTokenFormatterOptions,
    ...options,
  })
  return formatter.format(getTokenNumberValue({ value: BigInt(value ?? 0), decimals }))
}

export function formatPreciseAmount({
  amount,
  options,
}: { amount: number; options?: Intl.NumberFormatOptions }): string {
  const getFormatter = (innerOptions?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat('en-US', {
      ...defaultTokenFormatterOptions,
      ...innerOptions,
    })

  if (amount === 0) return '0'
  if (amount < 0.0001) return '<0.0001'
  if (amount < 0.01) return getFormatter({ minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(amount)
  if (amount < 0.1) return getFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(amount)
  if (amount < 1) return getFormatter().format(amount)
  return getFormatter({ ...options }).format(amount)
}

export function formatPreciseTokenValue(
  { value, decimals }: { value: string | bigint | undefined; decimals: number },
  options?: Intl.NumberFormatOptions
): string {
  const formattedTokenValue = getTokenNumberValue({ value: BigInt(value ?? 0), decimals })

  return formatPreciseAmount({ amount: formattedTokenValue, options })
}

const defaultFormatterOptions: Intl.NumberFormatOptions = {
  notation: 'compact',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as const

export function formatUSD(
  value: string | number,
  options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>
): string {
  const formatter = new Intl.NumberFormat('en-US', {
    ...defaultFormatterOptions,
    style: 'currency',
    currency: 'USD',
    ...options,
  })

  const numericValue = Number(value)
  const formattedValue = formatter.format(numericValue)

  if (numericValue !== 0) {
    const minFractionDigits = formatter.resolvedOptions().minimumFractionDigits as number
    const maxFractionDigits = formatter.resolvedOptions().maximumFractionDigits as number
    const fractionDigits = Math.min(minFractionDigits, maxFractionDigits)
    const zeroPattern = new RegExp(`^\\$0\\.0{${fractionDigits}}$`)
    if (zeroPattern.test(formattedValue)) {
      return `<$${0}.${'0'.repeat(fractionDigits - 1)}1`
    }
  }

  return formattedValue
}

export function formatAmount(amount: number, options?: Intl.NumberFormatOptions) {
  return Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    ...options,
  }).format(amount)
}
